import React from 'react';
import { graphql } from 'gatsby';
import { Layout, Seo } from '@layout';
import {
  HeroSection,
  ShopRegistrySection,
  ShopSection,
  RegistryCashFundSection,
  RegistryTopBarSection,
  GuestExperienceSection,
  SupportSection,
  RegistryFaqSection,
  ThankYouNoteSection,
} from '@sections/registry';
import { RecommendProducts } from '@sections/shared/GridView';
import { Box } from '@components/Box';
import { Flex } from '@components/Flex';
import { UserContentSection } from '@sections/registry/UserContentSection';
import { TestimonialsCarousel } from '@sections/registry/components/TestimonialsCarousel';
import { useTranslation } from 'react-i18next';
import { useRemoteFeatureFlag } from '@utils/remoteFeatureFlags/useRemoteFeatureFlag';
import { HeroSectionV2 } from '@sections/registry/HeroSection/HeroSectionV2';

const page = 'registry';

interface RegistryBlogs {
  id: string;
  blogs: {
    link: string;
    title: string;
    excerpt: string;
    mediaLink: string;
  }[];
}

interface BlissRegistryData {
  recommendCatalogHomeSections: {
    sections: {
      paginatedCatalogEntities: {
        edges: RecommendProducts;
      };
    }[];
  };
}

interface RegistryPageData {
  bliss: BlissRegistryData;
  featureBlogs: RegistryBlogs;
}

export const query = graphql`
  {
    bliss {
      recommendCatalogHomeSections {
        sections(offset: 1, limit: 1) {
          paginatedCatalogEntities(first: 20) {
            edges {
              node {
                ... on Bliss_ProductCatalogEntityItemGroup {
                  catalogItemGroupData {
                    tileViewItem {
                      id
                      title
                      brand {
                        name
                        publicUrlPath
                      }
                      price {
                        currency {
                          code
                          places
                        }
                        floatingPointDecimalString
                        valueInMinorUnits
                      }
                      publicUrlPath
                      photos {
                        url
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
    featureBlogs {
      id
      blogs {
        link
        title
        excerpt
        mediaLink
      }
    }
  }
`;

const Registry = ({ data }: { data: RegistryPageData }) => {
  const { t } = useTranslation();
  const marcomRegistryHeroExperiment = useRemoteFeatureFlag('marcomRegistryHeroExperiment');
  const isMarcomRegistryHeroExperimentLoading = marcomRegistryHeroExperiment.loading;
  const isMarcomRegistryHeroExperimentTreatment = marcomRegistryHeroExperiment.value === 'treatment';

  return (
    <Layout transparentHeaderButtonVariant="primeWeddingWebsites" headerScrollThresholdVh={35} page={page}>
      <Seo
        title={t('registry:seo.title')}
        canonicalURL={'https://withjoy.com/registry/'}
        description={t('registry:seo.content')}
        ogImage={'common/meta_share/registry.jpg'}
      />
      <Box style={{ background: 'linear-gradient(180deg, #FFF 0%, #FFF 84.5%, #F5F5F7 100%)' }}>
        <RegistryTopBarSection />
        {isMarcomRegistryHeroExperimentLoading ? (
          <Box style={{ height: '500px', width: '100%' }} /> // same height as Hero section
        ) : isMarcomRegistryHeroExperimentTreatment ? (
          <HeroSectionV2 />
        ) : (
          <HeroSection />
        )}
        <ShopRegistrySection />
      </Box>
      <Flex flexDirection="column" alignItems="center" style={{ backgroundColor: '#F5F5F7' }}>
        <ShopSection products={data.bliss.recommendCatalogHomeSections.sections[0].paginatedCatalogEntities.edges} />
        <RegistryCashFundSection />
        <GuestExperienceSection />
        <ThankYouNoteSection />
        <SupportSection />
        <RegistryFaqSection questionNodes={[...t('faq:questions.registry'), ...t('faq:questions.general')]} />
      </Flex>
      <UserContentSection />
      <TestimonialsCarousel testimonialsData={t('testimonials:registry')} />
    </Layout>
  );
};

export default Registry;
